// Product types and interfaces
export interface Product {
  id?: string;
  slug: string; // URL için benzersiz ID (örn: "abc", "xyz123")
  name: string;
  shortDescription: string; // Kısa açıklama
  detailedDescription: string; // Detaylı açıklama
  category: 'gelinlik' | 'abiye' | 'kinalik' | 'aksesuar';
  subcategory?: string; // A Kesim, Balık Kesim, vb.
  price: number;
  originalPrice?: number; // İndirim öncesi fiyat
  discount?: number; // İndirim yüzdesi
  currency: string; // Para birimi (TL, USD, EUR)
  isOnSale: boolean;
  images: string[]; // Firebase Storage URL'leri
  sizes: ProductSize[];
  stock: number;
  colors: string[];
  materials: string[]; // Kumaş türü
  features: string[]; // Özellikler
  tags: string[]; // Etiketler
  isActive: boolean;
  isFeatured: boolean; // Öne çıkan ürün
  createdAt: Date;
  updatedAt: Date;
  createdBy: string; // Admin kullanıcı ID'si
  seoTitle?: string;
  seoDescription?: string;
  
  // Kategoriye özel alanlar
  weddingDressDetails?: WeddingDressDetails;
  eveningDressDetails?: EveningDressDetails;
  hennaDressDetails?: HennaDressDetails;
}

// Gelinlik özel detayları
export interface WeddingDressDetails {
  modelType: string; // balık, prenses, a-kesim vb.
  necklineType: string; // kalp yaka, kayık yaka vb.
  veilCompatible: boolean; // duvak uygun mu
  backStyle: string; // düğme, ip vb.
  trainLength: string; // kuyruk uzunluğu
}

// Abiye özel detayları
export interface EveningDressDetails {
  sparkleLevel: string; // parıltı seviyesi
  eventType: string; // düğün, mezuniyet, nişan vb.
  strapType: string; // askılı, straplez vb.
}

// Kınalık özel detayları
export interface HennaDressDetails {
  embroideryType: string; // altın sim, boncuk, dantel vb.
  accessorySet: string; // tac kemer dahil mi?
  colorTheme: string; // renk teması
  kaftanLength: string; // kaftan uzunluğu
}

export interface ProductSize {
  size: string; // S, M, L, XL, vb.
  stock: number;
  measurements?: {
    bust?: number;
    waist?: number;
    hips?: number;
    length?: number;
  };
}

export interface ProductFilter {
  category?: string;
  subcategory?: string;
  minPrice?: number;
  maxPrice?: number;
  isOnSale?: boolean;
  isFeatured?: boolean;
  colors?: string[];
  sizes?: string[];
  materials?: string[];
}

export interface ProductSort {
  field: 'price' | 'createdAt' | 'name' | 'discount';
  direction: 'asc' | 'desc';
}

// Default product data
export const defaultProduct: Omit<Product, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'> = {
  name: '',
  shortDescription: '',
  detailedDescription: '',
  category: 'gelinlik',
  price: 0,
  currency: 'TL',
  isOnSale: false,
  images: [],
  sizes: [],
  stock: 0,
  colors: [],
  materials: [],
  features: [],
  tags: [],
  isActive: true,
  isFeatured: false,
  slug: ''
};

// Category options
export const categoryOptions = [
  { value: 'gelinlik', label: 'Gelinlik' },
  { value: 'abiye', label: 'Abiye' },
  { value: 'kinalik', label: 'Kınalık' },
  { value: 'aksesuar', label: 'Aksesuar' }
];

// Subcategory options by category
export const subcategoryOptions = {
  gelinlik: [
    { value: 'a-kesim', label: 'A Kesim' },
    { value: 'balik-kesim', label: 'Balık Kesim' },
    { value: 'prenses', label: 'Prenses Model' },
    { value: 'bohem', label: 'Bohem' },
    { value: 'tesettur', label: 'Tesettür' },
    { value: 'mermaid', label: 'Mermaid' },
    { value: 'ball-gown', label: 'Ball Gown' }
  ],
  abiye: [
    { value: 'gece', label: 'Gece Abiyesi' },
    { value: 'gunduz', label: 'Gündüz Abiyesi' },
    { value: 'cocktail', label: 'Cocktail' },
    { value: 'tesettur', label: 'Tesettür Abiye' },
    { value: 'klasik', label: 'Klasik' },
    { value: 'modern', label: 'Modern' }
  ],
  kinalik: [
    { value: 'klasik', label: 'Klasik Kınalık' },
    { value: 'modern', label: 'Modern Kınalık' },
    { value: 'tesettur', label: 'Tesettür Kınalık' },
    { value: 'bohem', label: 'Bohem Kınalık' }
  ],
  aksesuar: [
    { value: 'tac', label: 'Gelin Tacı' },
    { value: 'duvak', label: 'Duvak' },
    { value: 'ayakkabi', label: 'Gelin Ayakkabısı' },
    { value: 'canta', label: 'Gelin Çantası' },
    { value: 'takı', label: 'Takı' },
    { value: 'eldiven', label: 'Eldiven' }
  ]
};

// Size options
export const sizeOptions = [
  { value: '32', label: '32' },
  { value: '34', label: '34' },
  { value: '36', label: '36' },
  { value: '38', label: '38' },
  { value: '40', label: '40' },
  { value: '42', label: '42' },
  { value: '44', label: '44' },
  { value: '46', label: '46' },
  { value: '48', label: '48' },
  { value: '50', label: '50' }
];

// Color options
export const colorOptions = [
  { value: 'beyaz', label: 'Beyaz' },
  { value: 'krem', label: 'Krem' },
  { value: 'fildisi', label: 'Fildişi' },
  { value: 'champagne', label: 'Champagne' },
  { value: 'altin', label: 'Altın' },
  { value: 'gumus', label: 'Gümüş' },
  { value: 'siyah', label: 'Siyah' },
  { value: 'kirmizi', label: 'Kırmızı' },
  { value: 'mavi', label: 'Mavi' },
  { value: 'yesil', label: 'Yeşil' },
  { value: 'bordo', label: 'Bordo' },
  { value: 'zumrut', label: 'Zümrüt' },
  { value: 'lacivert', label: 'Lacivert' },
  { value: 'pembe', label: 'Pembe' },
  { value: 'mor', label: 'Mor' },
  { value: 'karisik', label: 'Karışık' }
];

// Material options
export const materialOptions = [
  { value: 'ipek', label: 'İpek' },
  { value: 'saten', label: 'Saten' },
  { value: 'tulle', label: 'Tül' },
  { value: 'lace', label: 'Dantel' },
  { value: 'organza', label: 'Organza' },
  { value: 'chiffon', label: 'Şifon' },
  { value: 'tafta', label: 'Tafta' },
  { value: 'kadife', label: 'Kadife' },
  { value: 'polyester', label: 'Polyester' },
  { value: 'viscose', label: 'Viskon' }
];

// Currency options
export const currencyOptions = [
  { value: 'TL', label: 'Türk Lirası (₺)' },
  { value: 'USD', label: 'Amerikan Doları ($)' },
  { value: 'EUR', label: 'Euro (€)' }
];

// Gelinlik özel seçenekleri
export const weddingDressOptions = {
  modelTypes: [
    { value: 'balik', label: 'Balık Kesim' },
    { value: 'prenses', label: 'Prenses Model' },
    { value: 'a-kesim', label: 'A Kesim' },
    { value: 'mermaid', label: 'Mermaid' },
    { value: 'ball-gown', label: 'Ball Gown' },
    { value: 'bohem', label: 'Bohem' },
    { value: 'klasik', label: 'Klasik' }
  ],
  necklineTypes: [
    { value: 'kalp-yaka', label: 'Kalp Yaka' },
    { value: 'kayik-yaka', label: 'Kayık Yaka' },
    { value: 'v-yaka', label: 'V Yaka' },
    { value: 'yuvarlak-yaka', label: 'Yuvarlak Yaka' },
    { value: 'askili', label: 'Askılı' },
    { value: 'straplez', label: 'Straplez' },
    { value: 'off-shoulder', label: 'Off Shoulder' }
  ],
  backStyles: [
    { value: 'dugme', label: 'Düğme' },
    { value: 'ip', label: 'İp' },
    { value: 'fermuar', label: 'Fermuar' },
    { value: 'korsaj', label: 'Korsaj' },
    { value: 'acik-sirt', label: 'Açık Sırt' }
  ],
  trainLengths: [
    { value: 'kuyruk-yok', label: 'Kuyruk Yok' },
    { value: 'kisa-kuyruk', label: 'Kısa Kuyruk' },
    { value: 'orta-kuyruk', label: 'Orta Kuyruk' },
    { value: 'uzun-kuyruk', label: 'Uzun Kuyruk' },
    { value: 'caterpillar', label: 'Caterpillar' }
  ]
};

// Abiye özel seçenekleri
export const eveningDressOptions = {
  sparkleLevels: [
    { value: 'mat', label: 'Mat' },
    { value: 'hafif-parilti', label: 'Hafif Parıltı' },
    { value: 'orta-parilti', label: 'Orta Parıltı' },
    { value: 'yuksek-parilti', label: 'Yüksek Parıltı' },
    { value: 'ultra-parilti', label: 'Ultra Parıltı' }
  ],
  eventTypes: [
    { value: 'dugun', label: 'Düğün' },
    { value: 'mezuniyet', label: 'Mezuniyet' },
    { value: 'nisan', label: 'Nişan' },
    { value: 'dogum-gunu', label: 'Doğum Günü' },
    { value: 'yilbasi', label: 'Yılbaşı' },
    { value: 'cocktail', label: 'Cocktail' },
    { value: 'galeri', label: 'Galeri' }
  ],
  strapTypes: [
    { value: 'askili', label: 'Askılı' },
    { value: 'straplez', label: 'Straplez' },
    { value: 'tek-askili', label: 'Tek Askılı' },
    { value: 'off-shoulder', label: 'Off Shoulder' },
    { value: 'halter', label: 'Halter' },
    { value: 'spaghetti', label: 'Spaghetti' }
  ]
};

// Kınalık özel seçenekleri
export const hennaDressOptions = {
  embroideryTypes: [
    { value: 'altin-sim', label: 'Altın Sim' },
    { value: 'boncuk', label: 'Boncuk' },
    { value: 'dantel', label: 'Dantel' },
    { value: 'nakis', label: 'Nakış' },
    { value: 'payet', label: 'Payet' },
    { value: 'inci', label: 'İnci' },
    { value: 'kristal', label: 'Kristal' }
  ],
  accessorySets: [
    { value: 'tac-kemer-dahil', label: 'Tac ve Kemer Dahil' },
    { value: 'sadece-tac', label: 'Sadece Tac' },
    { value: 'sadece-kemer', label: 'Sadece Kemer' },
    { value: 'aksesuar-yok', label: 'Aksesuar Yok' },
    { value: 'tam-set', label: 'Tam Set (Tac, Kemer, Eldiven)' }
  ],
  colorThemes: [
    { value: 'altin', label: 'Altın' },
    { value: 'gumus', label: 'Gümüş' },
    { value: 'kirmizi', label: 'Kırmızı' },
    { value: 'pembe', label: 'Pembe' },
    { value: 'mavi', label: 'Mavi' },
    { value: 'yesil', label: 'Yeşil' },
    { value: 'mor', label: 'Mor' },
    { value: 'karisik', label: 'Karışık' }
  ],
  kaftanLengths: [
    { value: 'kisa', label: 'Kısa' },
    { value: 'orta', label: 'Orta' },
    { value: 'uzun', label: 'Uzun' },
    { value: 'cok-uzun', label: 'Çok Uzun' }
  ]
};

// Utility functions for slug generation
export const generateSlug = (name: string): string => {
  // Türkçe karakterleri değiştir
  const turkishChars: { [key: string]: string } = {
    'ç': 'c', 'ğ': 'g', 'ı': 'i', 'ö': 'o', 'ş': 's', 'ü': 'u',
    'Ç': 'C', 'Ğ': 'G', 'İ': 'I', 'Ö': 'O', 'Ş': 'S', 'Ü': 'U'
  };
  
  let slug = name.toLowerCase();
  
  // Türkçe karakterleri değiştir
  Object.keys(turkishChars).forEach(char => {
    slug = slug.replace(new RegExp(char, 'g'), turkishChars[char]);
  });
  
  // Özel karakterleri temizle ve tire ile değiştir
  slug = slug.replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');
  
  // Başta ve sonda tire varsa temizle
  slug = slug.replace(/^-+|-+$/g, '');
  
  return slug;
};

export const generateUniqueSlug = (name: string, existingSlugs: string[]): string => {
  let baseSlug = generateSlug(name);
  let slug = baseSlug;
  let counter = 1;
  
  while (existingSlugs.includes(slug)) {
    slug = `${baseSlug}-${counter}`;
    counter++;
  }
  
  return slug;
};

export const generateRandomSlug = (length: number = 6): string => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};
